import { MAIN_MENU_BACKDROP_CSS } from './mainMenuBackdrop.js';
import { MAIN_MENU_SUPPORT_CSS } from './mainMenuSupportStyles.js';

const STYLE_ID = 'main-menu-polish-styles';

export function injectMainMenuStyles(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(STYLE_ID)?.remove();
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = MAIN_MENU_CSS;
  document.head.appendChild(style);
}

const MAIN_MENU_CSS = `
:root {
  --mm-blue-core: #1ea7ff;
  --mm-blue-bright: #6fdcff;
  --mm-blue-pure: #aef0ff;
  --mm-blue-glow: rgba(30, 167, 255, 1);
  --mm-red-core: #ff2a2a;
  --mm-red-bright: #ff7a5a;
  --mm-red-pure: #ffd6c8;
  --mm-red-glow: rgba(255, 42, 42, 1);
  --mm-purple-mid: #8a4cd6;
  --mm-amber: #ffe680;
  --mm-green: #6ed788;
  --mm-ink: #eef4ff;
  --mm-ink-muted: #a3b8d2;
  --mm-ink-faint: #6a7d96;
  --mm-panel: rgba(6, 10, 18, 0.88);
  --mm-panel-strong: rgba(6, 10, 18, 0.96);
  --mm-line: rgba(174, 240, 255, 0.22);
  --mm-line-strong: rgba(174, 240, 255, 0.55);
  --mm-font-display: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  --mm-font-ui: 'Rajdhani', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

#main-menu,
#network-lobby {
  position: absolute;
  inset: 0;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 30;
  overflow: hidden;
  background: #02060d;
  color: var(--mm-ink);
  font-family: var(--mm-font-ui);
  -webkit-font-smoothing: antialiased;
  user-select: none;
}

#main-menu *,
#network-lobby * {
  box-sizing: border-box;
}

${MAIN_MENU_BACKDROP_CSS}

.main-menu__card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
  width: min(620px, 92vw);
  max-height: calc(100vh - 28px);
  padding: 28px 34px 24px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(111, 220, 255, 0.3) transparent;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.35) 100%),
    radial-gradient(ellipse 100% 100% at 0% 50%, rgba(30, 130, 220, 0.16), transparent 60%),
    radial-gradient(ellipse 100% 100% at 100% 50%, rgba(220, 50, 50, 0.14), transparent 60%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.92), rgba(5, 9, 17, 0.96));
  border: 1px solid var(--mm-line);
  border-radius: 0;
  clip-path: polygon(18px 0, 100% 0, 100% calc(100% - 18px), calc(100% - 18px) 100%, 0 100%, 0 18px);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 44px rgba(30,167,255,0.12) inset,
    0 22px 70px rgba(0,0,0,0.72);
  animation: mm-card-in 540ms cubic-bezier(0.16, 0.84, 0.44, 1);
}

.main-menu__card--home {
  width: min(640px, 92vw);
  padding: 14px;
  gap: 12px;
  align-items: center;
  overflow: visible;
  background: transparent;
  border: 0;
  clip-path: none;
  box-shadow: none;
}

/* V4.2 — returning-player two-column layout. Widens the card and turns
 * the widget + actions stack into a side-by-side grid so the menu fits
 * a 720-800px viewport without zooming. Single-column flat layout
 * remains for first-session players (no widget = empty left column),
 * and the @media rule below re-stacks on narrow viewports. */
.main-menu__card--home.main-menu__card--two-col {
  width: min(900px, 96vw);
}
.main-menu__main-columns {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 18px;
  width: 100%;
  align-items: start;
  animation: mm-fade-up 1.4s 0.85s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
.main-menu__main-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.main-menu__main-col--right {
  align-items: stretch;
}
/* Inside the columns, the widget / online-stack / setup-panel should
 * fill their column instead of clipping at their stand-alone max-widths. */
.main-menu__main-columns .main-menu__widget,
.main-menu__main-columns .main-menu__secondary-stack,
.main-menu__main-columns .main-menu__setup-panel {
  max-width: none;
}
/* The columns wrapper kills its own animation delay on the children so
 * the widget doesn't double-animate (the widget had no animation; the
 * action row inherited the secondary-stack's own delay). Reset both
 * for consistency inside the grid. */
.main-menu__main-columns .main-menu__secondary-stack,
.main-menu__main-columns .main-menu__setup-panel {
  animation: none;
}
@media (max-width: 760px) {
  .main-menu__card--home.main-menu__card--two-col {
    width: min(640px, 92vw);
  }
  .main-menu__main-columns {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}

.main-menu__card::-webkit-scrollbar { width: 6px; }
.main-menu__card::-webkit-scrollbar-track { background: transparent; }
.main-menu__card::-webkit-scrollbar-thumb {
  background: rgba(111, 220, 255, 0.28);
  border-radius: 3px;
}

.main-menu__title-wrap {
  position: relative;
  text-align: center;
  margin-bottom: 2px;
  animation: mm-title-enter 1.3s cubic-bezier(0.16, 0.8, 0.24, 1) both;
}

.main-menu__signal-icon {
  display: block;
  width: 30px;
  height: 30px;
  margin: 0 auto 4px;
  color: var(--mm-blue-bright);
  filter:
    drop-shadow(0 0 6px var(--mm-blue-glow))
    drop-shadow(0 0 14px rgba(30, 167, 255, 0.45));
  animation: mm-signal-pulse 2.4s ease-in-out infinite;
}

.main-menu__title {
  margin: 0;
  font-family: var(--mm-font-display);
  font-weight: 900;
  letter-spacing: 0.02em;
  line-height: 0.92;
  text-align: center;
  text-transform: uppercase;
}

.main-menu__title--brand {
  display: block;
}

.main-menu__title--compact {
  color: #f8fcff;
  font-size: clamp(1.8rem, 4.5vw, 2.7rem);
  line-height: 1;
  text-shadow:
    0 0 1px rgba(255,255,255,1),
    0 0 8px rgba(95, 211, 255, 0.85),
    0 0 20px rgba(30, 167, 255, 0.45),
    0 2px 8px rgba(0,0,0,0.95);
}

.main-menu__title-word {
  position: relative;
  display: block;
  font-size: clamp(2.2rem, 5.4vw, 3.8rem);
  transform: skewX(-4deg);
}

.main-menu__title-text {
  position: relative;
  z-index: 2;
  display: inline-block;
}

.main-menu__title-ghost {
  position: absolute;
  inset: 0;
  z-index: 1;
  filter: blur(10px);
  opacity: 0.55;
}

.main-menu__title-word--blue .main-menu__title-text {
  color: #f8fcff;
  -webkit-text-stroke: 1px rgba(10, 40, 80, 0.55);
  text-shadow:
    0 0 1px rgba(255,255,255,1),
    0 0 3px rgba(200, 240, 255, 1),
    0 0 8px rgba(95, 211, 255, 1),
    0 0 16px rgba(30, 167, 255, 0.75),
    0 0 28px rgba(30, 167, 255, 0.4),
    0 1px 0 rgba(0, 20, 40, 0.9),
    0 3px 8px rgba(0, 0, 0, 0.95);
}

.main-menu__title-word--blue .main-menu__title-ghost {
  color: var(--mm-blue-core);
}

.main-menu__title-word--red .main-menu__title-text {
  color: #fff8f4;
  -webkit-text-stroke: 1px rgba(80, 10, 10, 0.55);
  text-shadow:
    0 0 1px rgba(255,255,255,1),
    0 0 3px rgba(255, 230, 220, 1),
    0 0 8px rgba(255, 122, 90, 1),
    0 0 16px rgba(255, 42, 42, 0.75),
    0 0 28px rgba(255, 42, 42, 0.4),
    0 1px 0 rgba(40, 10, 10, 0.9),
    0 3px 8px rgba(0, 0, 0, 0.95);
}

.main-menu__title-word--red .main-menu__title-ghost {
  color: var(--mm-red-core);
}

.main-menu__title-tag,
.main-menu__title-divider,
.main-menu__title-underline {
  display: none;
}

.main-menu__subtitle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 8px;
  font-family: var(--mm-font-display);
  color: var(--mm-ink-muted);
  font-size: 0.72rem;
  letter-spacing: 0.4em;
  line-height: 1.35;
  text-transform: uppercase;
  text-shadow: 0 2px 6px rgba(0,0,0,0.95);
  animation: mm-fade-up 1.4s 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.main-menu__subtitle-bar {
  flex: 0 0 auto;
  width: 50px;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--mm-ink-muted), transparent);
}

.main-menu__subtitle-blue {
  color: var(--mm-blue-bright);
  text-shadow: 0 0 8px var(--mm-blue-glow);
}

.main-menu__subtitle-red {
  color: var(--mm-red-bright);
  text-shadow: 0 0 8px var(--mm-red-glow);
}

.main-menu__actions-wrap {
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: stretch;
}

.main-menu__primary {
  width: 100%;
  max-width: 480px;
}

.main-menu__primary--hero {
  margin: 0;
}

.main-menu__secondary-stack {
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 100%;
  max-width: 380px;
  animation: mm-fade-up 1.4s 0.75s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.main-menu__dual {
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 100%;
}

.main-menu__btn {
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  min-height: 52px;
  padding: 0;
  overflow: visible;
  background: transparent;
  border: 0;
  color: var(--mm-ink);
  cursor: pointer;
  font-family: var(--mm-font-display);
  font-size: 0.98rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-align: center;
  text-transform: uppercase;
  outline: none;
  transition: transform 0.18s ease, color 0.2s ease, filter 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.main-menu__btn::before,
.main-menu__btn::after {
  content: "";
  position: absolute;
  pointer-events: none;
  clip-path: polygon(4% 0, 96% 0, 100% 50%, 96% 100%, 4% 100%, 0 50%);
}

.main-menu__btn::before {
  inset: 0;
  z-index: -2;
  background: linear-gradient(90deg,
    rgba(150, 200, 240, 0.6) 0%,
    rgba(220, 230, 245, 0.75) 50%,
    rgba(240, 150, 140, 0.6) 100%);
  transition: background 0.25s ease, filter 0.25s ease;
}

.main-menu__btn::after {
  inset: 1.5px;
  z-index: -1;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.10) 0%, transparent 28%, transparent 72%, rgba(0,0,0,0.35) 100%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.94) 0%, rgba(5, 9, 17, 0.94) 100%);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.06);
  backdrop-filter: blur(6px);
}

.main-menu__btn > * {
  position: relative;
  z-index: 2;
}

.main-menu__btn:hover,
.main-menu__btn:focus-visible {
  transform: translateY(-1px);
}

.main-menu__btn:hover::before,
.main-menu__btn:focus-visible::before {
  background: linear-gradient(90deg, var(--mm-blue-core) 0%, #c8d6e8 50%, var(--mm-red-core) 100%);
  filter: drop-shadow(0 0 14px rgba(120, 160, 220, 0.55));
}

.main-menu__btn:active {
  transform: translateY(0) scale(0.985);
}

.main-menu__btn-main,
.main-menu__btn-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: 100%;
  height: 100%;
  min-width: 0;
  text-shadow:
    0 0 8px rgba(255,255,255,0.35),
    0 2px 4px rgba(0,0,0,0.85);
}

.main-menu__btn-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  min-width: 0;
}

.main-menu__btn-copy {
  min-width: 0;
}

.main-menu__btn-sub {
  display: block;
  margin-top: 3px;
  color: var(--mm-ink-muted);
  font-family: var(--mm-font-ui);
  font-size: 0.76rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: none;
}

.main-menu__btn svg {
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  color: var(--mm-blue-bright);
  filter: drop-shadow(0 0 6px var(--mm-blue-glow));
}

.main-menu__btn--red svg,
.main-menu__btn--secondary svg {
  color: var(--mm-red-bright);
  filter: drop-shadow(0 0 6px var(--mm-red-glow));
}

.main-menu__btn--cta {
  height: 104px;
  margin-top: 20px;
  color: #fff;
  font-size: 2.15rem;
  font-weight: 900;
  letter-spacing: 0.17em;
  animation: mm-fade-up 1.4s 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.main-menu__btn--cta::before {
  clip-path: polygon(8% 0, 92% 0, 100% 50%, 92% 100%, 8% 100%, 0 50%);
  background: linear-gradient(90deg,
    var(--mm-blue-core) 0%,
    var(--mm-blue-bright) 18%,
    var(--mm-purple-mid) 50%,
    var(--mm-red-bright) 82%,
    var(--mm-red-core) 100%);
  filter:
    drop-shadow(0 0 16px rgba(150, 90, 230, 0.6))
    drop-shadow(0 0 32px rgba(80, 140, 230, 0.4));
  animation: mm-play-breathe 2.6s ease-in-out infinite;
}

.main-menu__btn--cta::after {
  inset: 3px;
  clip-path: polygon(8% 0, 92% 0, 100% 50%, 92% 100%, 8% 100%, 0 50%);
  background:
    linear-gradient(180deg, rgba(255,255,255,0.20) 0%, transparent 32%, transparent 68%, rgba(0,0,0,0.45) 100%),
    radial-gradient(ellipse 100% 100% at 0% 50%, rgba(30, 130, 220, 0.55), transparent 55%),
    radial-gradient(ellipse 100% 100% at 100% 50%, rgba(220, 50, 50, 0.55), transparent 55%),
    linear-gradient(180deg, rgba(11, 20, 34, 0.96) 0%, rgba(6, 9, 18, 0.96) 100%);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.14);
}

.main-menu__btn--cta:hover {
  transform: translateY(-2px) scale(1.015);
}

.main-menu__btn--cta:hover::before,
.main-menu__btn--cta:focus-visible::before {
  filter:
    drop-shadow(0 0 28px rgba(180, 110, 240, 0.95))
    drop-shadow(0 0 60px rgba(100, 160, 240, 0.7));
  animation-play-state: paused;
}

.main-menu__btn--cta:hover::after {
  background:
    linear-gradient(180deg, rgba(255,255,255,0.26) 0%, transparent 32%, transparent 68%, rgba(0,0,0,0.45) 100%),
    radial-gradient(ellipse 100% 100% at 0% 50%, rgba(50, 160, 240, 0.7), transparent 55%),
    radial-gradient(ellipse 100% 100% at 100% 50%, rgba(240, 70, 70, 0.7), transparent 55%),
    linear-gradient(180deg, rgba(14, 24, 40, 0.96) 0%, rgba(8, 12, 22, 0.96) 100%);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.22);
}

.main-menu__shimmer {
  position: absolute;
  inset: 4px;
  z-index: 1;
  overflow: hidden;
  clip-path: polygon(8% 0, 92% 0, 100% 50%, 92% 100%, 8% 100%, 0 50%);
  pointer-events: none;
  mix-blend-mode: screen;
}

.main-menu__shimmer::before {
  content: "";
  position: absolute;
  top: -18%;
  bottom: -18%;
  left: -42%;
  width: 34%;
  background:
    linear-gradient(100deg,
      transparent 0%,
      rgba(174, 240, 255, 0.18) 24%,
      rgba(255, 255, 255, 0.85) 48%,
      rgba(255, 122, 90, 0.26) 62%,
      transparent 100%);
  filter: blur(0.4px);
  animation: mm-shine-pass 2.9s ease-in-out infinite;
}

.main-menu__chev {
  display: inline-block;
  font-size: 1.45rem;
  font-weight: 700;
  opacity: 0.95;
}

.main-menu__chev--left {
  color: var(--mm-blue-bright);
  text-shadow: 0 0 12px var(--mm-blue-glow);
  animation: mm-chev-pulse-l 1.8s ease-in-out infinite;
}

.main-menu__chev--right {
  color: var(--mm-red-bright);
  text-shadow: 0 0 12px var(--mm-red-glow);
  animation: mm-chev-pulse-r 1.8s 0.9s ease-in-out infinite;
}

.main-menu__btn--secondary,
.main-menu__btn--primary {
  height: 44px;
  font-size: 0.82rem;
  letter-spacing: 0.16em;
}

.main-menu__btn--secondary .main-menu__btn-main,
.main-menu__btn--primary .main-menu__btn-main {
  gap: 10px;
}

.main-menu__btn--secondary svg,
.main-menu__btn--primary svg {
  width: 17px;
  height: 17px;
}

.main-menu__btn--secondary .main-menu__btn-sub,
.main-menu__btn--primary .main-menu__btn-sub {
  margin-top: 1px;
  font-size: 0.66rem;
  line-height: 1.05;
  opacity: 0.82;
}

.main-menu__btn--primary::before {
  background: linear-gradient(90deg, rgba(30,167,255,0.78), rgba(210,225,245,0.75), rgba(255,122,90,0.52));
}

/* V1.8 — server cold-start indicator. /health preflight failed or timed
 * out (2 s budget) so the player gets advance warning instead of a
 * surprise 15 s connect timeout. Click still goes through; the lobby's
 * existing retry handles the slow path. */
.main-menu__btn--warming {
  opacity: 0.72;
  filter: grayscale(0.35);
}
.main-menu__btn--warming .main-menu__btn-sub {
  color: var(--mm-amber);
  opacity: 1;
}

/* V1.8 — returning-player widget: welcome-back banner + daily challenge
 * progress. Only renders for returning sessions; first-time players
 * never see this row. V2 Tempo lifted the inner styling into
 * .main-menu__profile (below); the outer widget is now a thin neutral
 * frame so the rich profile card carries the visual weight without
 * fighting the menu's blue/red rim. */
.main-menu__widget {
  width: 100%;
  margin: 8px 0;
  padding: 4px;
  border-radius: 0;
  background: transparent;
  border: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
/* V2 Tempo — welcome-back banner restyled as a thin neon ribbon that
 * pairs with the profile card below. Sits in the same column, uses the
 * menu's amber accent so it reads as "session-state news" rather than a
 * stat. */
.main-menu__widget-welcome {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: linear-gradient(90deg, rgba(255, 230, 128, 0.10), rgba(110, 215, 136, 0.08));
  border: 1px solid rgba(255, 230, 128, 0.32);
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
  font-family: var(--mm-font-display);
  font-size: 0.66rem;
  letter-spacing: 0.10em;
  color: var(--mm-amber);
  text-transform: none;
  position: relative;
}
.main-menu__widget-welcome::before {
  content: '◢';
  color: var(--mm-amber);
  font-size: 0.7rem;
  opacity: 0.75;
  margin-right: 2px;
}

/* V2 Tempo — daily challenges block: same hex/scanline frame as the
 * profile card so the entire returning-player surface reads as one
 * "Commander console" instead of two disconnected boxes. */
.main-menu__widget-header {
  font-family: var(--mm-font-display);
  font-size: 0.62rem;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: var(--mm-blue-bright);
  padding-bottom: 5px;
  border-bottom: 1px solid rgba(174, 240, 255, 0.18);
  margin: 4px 0 2px;
}
.main-menu__widget-list {
  margin: 0;
  padding: 10px 14px;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background:
    radial-gradient(ellipse 80% 100% at 0% 0%, rgba(30, 167, 255, 0.10), transparent 65%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.85), rgba(5, 9, 17, 0.92));
  border: 1px solid var(--mm-line);
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
  box-shadow: 0 0 0 1px rgba(255,255,255,0.05) inset, 0 6px 18px rgba(0,0,0,0.45);
  position: relative;
}
.main-menu__widget-list::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(180deg, rgba(174,240,255,0.035) 1px, transparent 1px);
  background-size: 100% 20px;
  opacity: 0.5;
  pointer-events: none;
}
.main-menu__widget-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  color: var(--mm-ink-muted);
  position: relative;
  z-index: 1;
}
.main-menu__widget-item--done .main-menu__widget-label {
  color: var(--mm-green);
  text-decoration: line-through;
  opacity: 0.85;
}
.main-menu__widget-item--done .main-menu__widget-status {
  color: var(--mm-green);
  text-shadow: 0 0 6px rgba(110, 215, 136, 0.45);
}
.main-menu__widget-status {
  min-width: 36px;
  text-align: right;
  font-family: var(--mm-font-display);
  color: var(--mm-amber);
}

/* V1.9 — XP / level surface on the returning-player widget. The bar
 * occupies the same row width as the daily-challenge list so the widget
 * reads as a vertical stack of equal-width strips. */
.main-menu__widget-xp {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 0 4px;
  border-bottom: 1px solid rgba(150, 190, 230, 0.12);
  margin-bottom: 2px;
}
.main-menu__widget-xp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
}
.main-menu__widget-xp-level {
  font-family: var(--mm-font-display);
  color: var(--mm-cyan, #6ad8ff);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-size: 0.66rem;
}
.main-menu__widget-xp-progress {
  color: var(--mm-ink-muted);
  font-size: 0.62rem;
  letter-spacing: 0.06em;
}
.main-menu__widget-xp-bar {
  height: 5px;
  background: rgba(150, 190, 230, 0.10);
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid rgba(106, 216, 255, 0.20);
}
.main-menu__widget-xp-fill {
  height: 100%;
  background: linear-gradient(90deg, #6ad8ff, #f5e76e);
  border-radius: 999px;
  transition: width 480ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

/* V1.9 — lifetime W/L + last-match strip. Single-line layout with the
 * win count on the left and the "last result" on the right. */
.main-menu__widget-history {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  color: var(--mm-ink-muted);
  padding: 4px 0;
  border-bottom: 1px solid rgba(150, 190, 230, 0.12);
}
.main-menu__widget-hist-pair {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--mm-font-display);
}
.main-menu__widget-hist-w { color: var(--mm-cyan, #6ad8ff); }
.main-menu__widget-hist-l { color: #ff8a70; }
.main-menu__widget-hist-sep { color: rgba(150, 190, 230, 0.45); }
.main-menu__widget-hist-last {
  font-size: 0.62rem;
  letter-spacing: 0.06em;
}
.main-menu__widget-hist-win { color: var(--mm-cyan, #6ad8ff); font-weight: 700; }
.main-menu__widget-hist-loss { color: #ff8a70; font-weight: 700; }

/* V2 Tempo — Commander Profile card. Replaces the old plain XP row in
 * the returning-player widget with a stage-grade surface that matches
 * the menu's neon hex aesthetic. Lives INSIDE .main-menu__widget so
 * the green-frame container still owns the outer chrome. */
.main-menu__profile {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px 14px;
  margin: 4px 0 6px;
  background:
    radial-gradient(ellipse 80% 100% at 0% 0%, rgba(30, 167, 255, 0.14), transparent 65%),
    radial-gradient(ellipse 60% 100% at 100% 100%, rgba(255, 122, 90, 0.10), transparent 65%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.92), rgba(5, 9, 17, 0.96));
  border: 1px solid var(--mm-line);
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 30px rgba(30,167,255,0.10) inset,
    0 8px 22px rgba(0,0,0,0.55);
  position: relative;
  overflow: hidden;
}
.main-menu__profile::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(180deg, rgba(174,240,255,0.04) 1px, transparent 1px);
  background-size: 100% 22px;
  opacity: 0.5;
  pointer-events: none;
}

.main-menu__profile-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  position: relative;
  z-index: 1;
}
.main-menu__profile-level {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.main-menu__profile-level-num {
  font-family: var(--mm-font-display);
  font-size: 2.6rem;
  line-height: 1;
  color: var(--mm-blue-pure);
  text-shadow:
    0 0 14px rgba(110, 220, 255, 0.55),
    0 0 28px rgba(30, 167, 255, 0.35);
  font-weight: 700;
  letter-spacing: 0.02em;
}
.main-menu__profile-level-word {
  font-family: var(--mm-font-display);
  font-size: 0.74rem;
  letter-spacing: 0.32em;
  color: var(--mm-blue-bright);
  text-transform: uppercase;
}
.main-menu__profile-next {
  text-align: right;
  font-family: var(--mm-font-display);
}
.main-menu__profile-next-label {
  font-size: 0.58rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--mm-ink-faint);
}
.main-menu__profile-next-value {
  font-size: 0.78rem;
  letter-spacing: 0.10em;
  color: var(--mm-amber);
  text-shadow: 0 0 10px rgba(255, 230, 128, 0.30);
}

/* V2 Tempo — Rank chip on the profile card. Mirrors the LEVEL block
 * on the left but uses tier-colored glow so the chip itself reads as
 * a status emblem. */
.main-menu__profile-rank {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  padding: 6px 12px;
  background: rgba(10, 19, 34, 0.55);
  border: 1px solid rgba(174, 240, 255, 0.30);
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
}
.main-menu__profile-rank-name {
  font-family: var(--mm-font-display);
  font-size: 1.0rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  line-height: 1;
}
.main-menu__profile-rank-elo {
  font-family: var(--mm-font-display);
  font-size: 0.62rem;
  letter-spacing: 0.10em;
  color: var(--mm-ink-muted);
}

/* Rank progress bar — same vertical rhythm as the XP bar below, but
 * the fill is tinted with the tier color so the player visually maps
 * "rising bar = climbing the ladder". */
.main-menu__profile-rankbar-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  z-index: 1;
}
.main-menu__profile-rankbar {
  position: relative;
  height: 6px;
  background: rgba(5, 9, 17, 0.85);
  border: 1px solid rgba(174, 240, 255, 0.18);
  border-radius: 1px;
  overflow: hidden;
}
.main-menu__profile-rankbar-fill {
  height: 100%;
  transition: width 480ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.main-menu__profile-rankbar-foot {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  font-family: var(--mm-font-display);
  font-size: 0.58rem;
  letter-spacing: 0.10em;
}
.main-menu__profile-rankbar-peak {
  color: var(--mm-amber);
  text-shadow: 0 0 8px rgba(255, 230, 128, 0.30);
}

.main-menu__profile-xp-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-family: var(--mm-font-display);
  font-size: 0.60rem;
  letter-spacing: 0.10em;
  position: relative;
  z-index: 1;
}
.main-menu__profile-xp-label {
  color: var(--mm-blue-bright);
  letter-spacing: 0.20em;
}

.main-menu__profile-xpbar-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  z-index: 1;
}
.main-menu__profile-xpbar {
  position: relative;
  height: 9px;
  background: rgba(5, 9, 17, 0.85);
  border: 1px solid rgba(106, 216, 255, 0.30);
  border-radius: 1px;
  overflow: hidden;
}
.main-menu__profile-xpbar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--mm-blue-core), var(--mm-blue-bright) 60%, var(--mm-amber));
  box-shadow:
    0 0 14px rgba(30, 167, 255, 0.45),
    0 0 4px rgba(255, 230, 128, 0.35);
  transition: width 480ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.main-menu__profile-xpbar-shimmer {
  position: absolute;
  top: 0;
  width: 16px;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent);
  filter: blur(1px);
  pointer-events: none;
  animation: mm-profile-shimmer 1800ms linear infinite;
  margin-left: -16px;
  opacity: 0.7;
}
.main-menu__profile-xpbar-foot {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  font-family: var(--mm-font-display);
  font-size: 0.62rem;
  letter-spacing: 0.10em;
}
.main-menu__profile-xpbar-pos {
  color: var(--mm-blue-bright);
}
.main-menu__profile-xpbar-tease {
  color: var(--mm-ink-muted);
  font-size: 0.62rem;
}
.main-menu__profile-xpbar-tease strong {
  color: var(--mm-amber);
  font-weight: 700;
}

.main-menu__profile-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  position: relative;
  z-index: 1;
}
.main-menu__profile-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 4px;
  background: rgba(10, 19, 34, 0.55);
  border: 1px solid rgba(174, 240, 255, 0.15);
  clip-path: polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px);
}
.main-menu__profile-cell-num {
  font-family: var(--mm-font-display);
  font-size: 1.05rem;
  color: var(--mm-ink);
  letter-spacing: 0.04em;
  line-height: 1.1;
}
.main-menu__profile-cell-num--cyan { color: var(--mm-blue-bright); }
.main-menu__profile-cell-num--amber { color: var(--mm-amber); }
.main-menu__profile-cell-lbl {
  font-size: 0.54rem;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: var(--mm-ink-faint);
  margin-top: 4px;
  text-align: center;
}

.main-menu__profile-form {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  position: relative;
  z-index: 1;
}
.main-menu__profile-form-lbl {
  font-family: var(--mm-font-display);
  font-size: 0.58rem;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: var(--mm-ink-faint);
}
.main-menu__profile-form-row {
  display: inline-flex;
  gap: 3px;
}
.main-menu__profile-form-cell {
  width: 14px;
  height: 14px;
  border-radius: 1px;
  border: 1px solid rgba(174,240,255,0.18);
  display: inline-block;
}
.main-menu__profile-form-cell--w {
  background: var(--mm-blue-core);
  border-color: var(--mm-blue-bright);
  box-shadow: 0 0 6px rgba(30,167,255,0.55);
}
.main-menu__profile-form-cell--l {
  background: var(--mm-red-core);
  border-color: var(--mm-red-bright);
  box-shadow: 0 0 6px rgba(255,42,42,0.45);
}
.main-menu__profile-form-cell--empty {
  background: transparent;
  border-color: rgba(174,240,255,0.10);
}

/* V2 Tempo — Weekly Modifier chip. Sits between the profile card and
 * the daily challenges. Hex chip on the left, modifier name in the
 * middle (tier color), description below in muted text. */
.main-menu__weekly {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  gap: 4px 12px;
  align-items: center;
  padding: 8px 12px;
  background:
    radial-gradient(ellipse 60% 100% at 0% 50%, color-mix(in srgb, var(--weekly-color) 18%, transparent), transparent 70%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.85), rgba(5, 9, 17, 0.92));
  border: 1px solid color-mix(in srgb, var(--weekly-color) 45%, rgba(174,240,255,0.18));
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
  box-shadow: 0 0 18px var(--weekly-glow), 0 4px 14px rgba(0,0,0,0.4);
}
.main-menu__weekly-chip {
  grid-row: 1;
  grid-column: 1;
  font-family: var(--mm-font-display);
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  padding: 3px 7px;
  border: 1px solid;
  clip-path: polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px);
  background: rgba(5, 9, 17, 0.65);
}
.main-menu__weekly-name {
  grid-row: 1;
  grid-column: 2;
  font-family: var(--mm-font-display);
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}
.main-menu__weekly-desc {
  grid-row: 2;
  grid-column: 1 / -1;
  font-family: var(--mm-font-ui);
  font-size: 0.66rem;
  letter-spacing: 0.04em;
  color: var(--mm-ink-muted);
  line-height: 1.3;
}

@keyframes mm-profile-shimmer {
  0%   { margin-left: -20px; opacity: 0.0; }
  20%  { opacity: 0.7; }
  80%  { opacity: 0.7; }
  100% { margin-left: 0; opacity: 0.0; }
}

.main-menu__btn--secondary::before,
.main-menu__btn--red::before {
  background: linear-gradient(90deg, rgba(255,122,90,0.55), rgba(210,225,245,0.72), rgba(30,167,255,0.52));
}

.main-menu__bottom-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  width: 100%;
  animation: mm-fade-up 1.4s 1.05s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.main-menu__btn--utility {
  min-height: 36px;
  color: var(--mm-ink-muted);
  font-size: 0.62rem;
  letter-spacing: 0.15em;
}

.main-menu__btn--utility::before {
  background: rgba(150, 190, 230, 0.32);
}

.main-menu__btn--utility:hover,
.main-menu__btn--utility:focus-visible {
  color: var(--mm-ink);
}

.main-menu__setup-panel {
  width: 100%;
  max-width: 360px;
  padding: 8px 10px;
  background: rgba(5, 10, 18, 0.36);
  border: 1px solid rgba(174, 240, 255, 0.13);
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
  box-shadow: inset 0 0 18px rgba(30, 167, 255, 0.05);
  animation: mm-fade-up 1.4s 0.92s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.main-menu__setup-panel .main-menu__bottom-row {
  gap: 6px;
  margin-top: 7px;
  animation: none;
}

.main-menu__setup-panel .main-menu__btn--utility {
  min-height: 30px;
}

.main-menu__setup-panel .main-menu__btn--utility svg {
  width: 14px;
  height: 14px;
}

.main-menu__setup-panel .main-menu__btn-content {
  gap: 8px;
}

.main-menu__setup-grid {
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 8px;
  width: 100%;
}

.main-menu__setup-column {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.main-menu__diff-label {
  position: relative;
  margin: 0;
  padding-left: 10px;
  color: var(--mm-ink-muted);
  font-family: var(--mm-font-display);
  font-size: 0.58rem;
  letter-spacing: 0.15em;
  line-height: 1.25;
  text-transform: uppercase;
}

.main-menu__diff-label::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  width: 5px;
  height: 5px;
  transform: translateY(-50%);
  background: var(--mm-blue-bright);
  box-shadow: 0 0 8px var(--mm-blue-glow);
}

.main-menu__diff-group {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px;
}

.main-menu__diff-group--maps {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.main-menu__diff-btn {
  min-width: 0;
  min-height: 30px;
  padding: 5px 7px;
  overflow: hidden;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.08), transparent 52%, rgba(0,0,0,0.26)),
    rgba(8, 15, 26, 0.82);
  border: 1px solid rgba(174, 240, 255, 0.2);
  border-radius: 0;
  color: var(--mm-ink-muted);
  cursor: pointer;
  font-family: var(--mm-font-display);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-overflow: clip;
  text-transform: uppercase;
  white-space: nowrap;
  transition: border-color 0.16s ease, color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.main-menu__diff-btn:hover,
.main-menu__diff-btn:focus-visible {
  border-color: var(--mm-blue-bright);
  color: var(--mm-ink);
  box-shadow: 0 0 10px rgba(111, 220, 255, 0.2);
  outline: none;
  transform: translateY(-1px);
}

.main-menu__diff-btn--selected {
  border-color: var(--mm-blue-bright);
  color: #f8fcff;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.14), transparent 48%, rgba(0,0,0,0.28)),
    radial-gradient(ellipse 100% 120% at 0% 50%, rgba(30, 167, 255, 0.32), transparent 58%),
    rgba(8, 15, 26, 0.94);
  box-shadow:
    0 0 14px rgba(111, 220, 255, 0.28),
    inset 0 0 10px rgba(111, 220, 255, 0.14);
}

.main-menu__diff-blurb {
  display: none;
  min-height: 0;
  color: var(--mm-ink-muted);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.25;
}

.main-menu__separator {
  display: none;
}

.main-menu__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  max-width: 360px;
  color: var(--mm-ink-muted);
  font-family: var(--mm-font-display);
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  animation: mm-fade-up 1.4s 1.18s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.main-menu__footer-left {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
}

.main-menu__footer-left::before {
  content: "";
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--mm-green);
  box-shadow: 0 0 7px var(--mm-green);
  animation: mm-live-blink 2s ease-in-out infinite;
}

.main-menu__footer-links {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.main-menu__footer a {
  color: var(--mm-ink-muted);
  text-decoration: none;
  transition: color 0.16s ease, text-shadow 0.16s ease;
}

.main-menu__footer a:hover,
.main-menu__footer a:focus-visible {
  color: var(--mm-blue-bright);
  text-shadow: 0 0 8px var(--mm-blue-glow);
  outline: none;
}

.main-menu__help {
  color: var(--mm-ink-muted);
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.45;
  text-align: center;
}

.network-lobby__code,
.network-lobby__status,
.network-lobby__opt-in,
.network-lobby__buttons {
  position: relative;
  z-index: 2;
}

.network-lobby__code {
  color: var(--mm-amber);
  font-family: var(--mm-font-display);
  font-size: clamp(1.4rem, 5vw, 2rem);
  letter-spacing: 0.22em;
  text-align: center;
  text-shadow: 0 0 14px rgba(255, 230, 128, 0.32);
}

.network-lobby__status {
  color: var(--mm-ink-muted);
  font-family: var(--mm-font-display);
  font-size: 0.82rem;
  letter-spacing: 0.12em;
  text-align: center;
  text-transform: uppercase;
}

.network-lobby__status[data-status="idle"] { color: var(--mm-amber); }
.network-lobby__status[data-status="connected"] { color: var(--mm-green); }
.network-lobby__status[data-status="waiting"] { color: var(--mm-blue-bright); }
.network-lobby__status[data-status="bot_filled"] { color: var(--mm-red-bright); }
.network-lobby__status[data-status="failed"] { color: #ff9380; }

.network-lobby__buttons {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 4px;
}

.network-lobby__opt-in {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 42px;
  padding: 8px 10px;
  color: var(--mm-ink-muted);
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
}

.network-lobby__opt-in input {
  width: 18px;
  height: 18px;
  accent-color: var(--mm-blue-bright);
}

/* Shared modal/menu surfaces */
#settings-panel,
#profile-panel,
#end-of-match,
#doctrine-picker,
#leave-match-overlay,
#signalclash-intro {
  background: rgba(2, 6, 13, 0.78) !important;
  color: var(--mm-ink) !important;
  font-family: var(--mm-font-ui) !important;
  backdrop-filter: blur(4px);
}

.settings__card,
.pp__card,
.eom__card,
.dp__card,
.leave-match-card,
.signalclash-intro__card {
  background:
    linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.36) 100%),
    radial-gradient(ellipse 100% 100% at 0% 50%, rgba(30, 130, 220, 0.16), transparent 60%),
    radial-gradient(ellipse 100% 100% at 100% 50%, rgba(220, 50, 50, 0.14), transparent 60%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.95), rgba(5, 9, 17, 0.97)) !important;
  border: 1px solid var(--mm-line) !important;
  border-radius: 0 !important;
  clip-path: polygon(18px 0, 100% 0, 100% calc(100% - 18px), calc(100% - 18px) 100%, 0 100%, 0 18px);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 44px rgba(30,167,255,0.12) inset,
    0 22px 70px rgba(0,0,0,0.72) !important;
}

.settings__title,
.pp__title,
.dp__title,
.signalclash-intro__title,
.leave-match-title {
  color: #f8fcff !important;
  font-family: var(--mm-font-display) !important;
  letter-spacing: 0.14em !important;
  text-shadow:
    0 0 1px rgba(255,255,255,1),
    0 0 8px rgba(95, 211, 255, 0.85),
    0 0 20px rgba(30, 167, 255, 0.45),
    0 2px 8px rgba(0,0,0,0.95) !important;
}

.eom__banner {
  font-family: var(--mm-font-display) !important;
  font-weight: 900 !important;
}

.settings__section,
.pp__header,
.pp__section-label,
.eom__stats-table {
  border-color: rgba(174, 240, 255, 0.18) !important;
}

.settings__section-heading,
.settings__row-label,
.pp__section-label,
.pp__stat-lbl,
.eom__stats-row--header,
.eom__stats-cell--label,
.eom__spark-label,
.signalclash-intro__chip {
  color: var(--mm-ink-muted) !important;
  font-family: var(--mm-font-display) !important;
  letter-spacing: 0.12em !important;
}

.settings__range,
.settings__checkbox,
.network-lobby__opt-in input {
  accent-color: var(--mm-blue-bright) !important;
}

.settings__diff-btn,
.settings__btn,
.pp__close-btn,
.eom__btn,
.leave-match-btn,
.signalclash-intro__cta {
  border-radius: 0 !important;
  border: 1px solid rgba(174, 240, 255, 0.28) !important;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.10) 0%, transparent 32%, transparent 68%, rgba(0,0,0,0.35) 100%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.94) 0%, rgba(5, 9, 17, 0.94) 100%) !important;
  color: var(--mm-ink) !important;
  font-family: var(--mm-font-display) !important;
  text-transform: uppercase;
  transition: transform 0.16s ease, border-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease !important;
}

.settings__diff-btn:hover,
.settings__btn:hover,
.pp__close-btn:hover,
.eom__btn:hover,
.leave-match-btn:hover,
.signalclash-intro__cta:hover {
  border-color: var(--mm-blue-bright) !important;
  color: var(--mm-blue-bright) !important;
  box-shadow: 0 0 14px rgba(111, 220, 255, 0.24) !important;
  transform: translateY(-1px);
}

.settings__diff-btn--selected,
.settings__btn--primary,
.eom__btn--primary,
.signalclash-intro__cta {
  border-color: var(--mm-blue-bright) !important;
  color: #fff !important;
  box-shadow: 0 0 16px rgba(111, 220, 255, 0.26), inset 0 0 10px rgba(111, 220, 255, 0.14) !important;
}

.leave-match-btn--leave {
  border-color: rgba(255, 122, 90, 0.62) !important;
  color: var(--mm-red-bright) !important;
}

.leave-match-btn--leave:hover {
  border-color: var(--mm-red-bright) !important;
  color: #fff !important;
  box-shadow: 0 0 16px rgba(255, 122, 90, 0.3) !important;
}

.dp__btn,
.pp__stat-cell,
.pp__badge,
.eom__mvp-row,
.eom__spark-svg,
.signalclash-intro__num {
  border-radius: 0 !important;
  border-color: rgba(174, 240, 255, 0.2) !important;
  background: rgba(8, 15, 26, 0.72) !important;
}

.dp__btn:hover {
  border-color: var(--mm-blue-bright) !important;
  box-shadow: 0 0 14px rgba(111, 220, 255, 0.2);
}

.dp__btn-name,
.settings__diff-blurb,
.settings__help,
.pp__hist-detail,
.eom__detail,
.eom__mvp-detail,
.eom__spark-caption,
.signalclash-intro__item-body,
.leave-match-online-note {
  color: var(--mm-ink-muted) !important;
}

${MAIN_MENU_SUPPORT_CSS}

/* V4.1 — first-session menu cohort. The Customize toggle + Settings row
 * replaces the full menu for players who have never completed a match.
 * The button styling reuses .main-menu__btn--utility; this rule only
 * lays out the row so the inline-style fallback in MainMenuScene
 * doesn't have to. */
.main-menu__first-session-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 10px;
  flex-wrap: wrap;
}
.main-menu__card--first-session .main-menu__primary--hero {
  /* Slightly more breathing room above PLAY NOW when it's the only
   * dominant CTA on screen — feels like the lobby of a console game
   * instead of a settings page with a button at the top. */
  margin-top: 8px;
}
`;
